<template>
  <div ref="el" v-editable="blok" class="item-content-head" :class="{ 'is-small': isSmall }">
    <!-- Headline -->
    <h3
      v-if="isSmall"
      ref="headline"
      :class="['heading-main-small', { 'is-invisible': !didFadeIn }]"
      v-html="headline"></h3>
    <h2 v-else ref="headline" :class="['heading-main-large', { 'is-invisible': !didFadeIn }]" v-html="headline"></h2>

    <!-- Subline -->
    <p v-if="blok.subline" ref="subline" class="subline heading-subline" :class="{ 'is-invisible': !didFadeIn }">
      {{ blok.subline }}
    </p>

    <!-- Action -->
    <div v-if="action" ref="action" class="action">
      <UtilLinkItemIcon v-if="action.component === 'util_link-overlay-item'" :blok="action" />
      <UtilLinkItemSubtext v-if="action.component === 'util_link-item-subtext'" :blok="action" />
      <UtilButton v-if="action.component === 'util_button'" :blok="action" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import { RichTextResolver } from '@storyblok/vue';
import { useScroll, useTransition } from '@seb-platform/shared/composables';
import { replaceHtmlEntities } from '@seb-platform/shared/utils/text';
import UtilButton from '~/components/storyblok/utils/UtilButton/UtilButton.vue';
import UtilLinkItemIcon from '~/components/storyblok/utils/UtilLinkItemIcon/UtilLinkItemIcon.vue';
import UtilLinkItemSubtext from '~/components/storyblok/utils/UtilLinkItemSubtext/UtilLinkItemSubtext.vue';
import type { ItemContentHeadStoryblok } from '~/types/storyblok-generated';

gsap.registerPlugin(ScrollTrigger);

const props = defineProps<{
  blok: ItemContentHeadStoryblok;
  staggerLines?: boolean;
}>();

const { inViewport, fadeSlide } = useTransition();
const { state: scrollState } = useScroll();

const $el = useTemplateRef('el');
const $headline = useTemplateRef<HTMLHeadingElement>('headline');
const $subline = useTemplateRef('subline');
const $action = useTemplateRef('action');

const didFadeIn = ref(false);

const resolver = new RichTextResolver();
const headline = computed<string | null>(() => {
  if (!props.blok.headline) {
    return null;
  }
  let html = resolver.render(props.blok.headline);
  html = replaceHtmlEntities(html);
  html = html.replace(/^<p>/, '').replace(/<\/p>$/, ''); // remove the most outer <p> tag
  return html;
});
const isSmall = computed(() => props.blok.headline_tag === 'h3');
const action = computed(() => {
  const action = props.blok.actions?.[0];
  if (action?.component === 'util_button') {
    action.size = 'large';
  }
  return action;
});
const animationState = { headlineVisible: false };
let timeout: number;

function fadeIn() {
  animationState.headlineVisible = true;
  if (!$el.value) {
    return;
  }

  fadeSlide([$headline.value, $subline.value, $action.value].filter(entry => !!entry));
  clearTimeout(timeout);
  timeout = window.setTimeout(() => {
    // Makes all elements visible to be editable in the visual editor of Storyblok
    didFadeIn.value = true;
  }, 1000);
}

function watchPageScroll() {
  if (animationState.headlineVisible || !$el.value) {
    return;
  }
  if (inViewport($el.value)) {
    fadeIn();
  }
}

watch(() => scrollState.top, watchPageScroll);

onBeforeUnmount(() => {
  clearTimeout(timeout);
});

onMounted(() => {
  void nextTick(watchPageScroll);
});
</script>

<style src="./ItemContentHead.scss" lang="scss" scoped />
